import React from 'react';
import { Card } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { AP_COPY } from '../adminPersonnelCopy';
import './ActivityFeed.css';

/**
 * ActivityFeed - Flux d'activités récentes avec timeline
 * 
 * @param {array} activities - Liste des activités
 *   [{
 *     id, 
 *     type: 'user' | 'company' | 'system' | 'alert',
 *     icon, 
 *     color, 
 *     title, 
 *     description, 
 *     timestamp,
 *     user
 *   }]
 * @param {boolean} loading - État de chargement
 * @param {number} maxItems - Nombre maximum d'activités à afficher
 * @param {function} onViewAll - Callback pour "Voir tout"
 */
const ActivityFeed = ({ 
  activities = [], 
  loading = false,
  maxItems = 5,
  onViewAll
}) => {
  const displayedActivities = activities.slice(0, maxItems);

  const getActivityIcon = (type) => {
    const icons = {
      user: 'userPlus',
      company: 'building',
      system: 'settings',
      alert: 'alertCircle',
      edit: 'edit',
      delete: 'trash',
      success: 'checkCircle',
      warning: 'alertTriangle'
    };
    return icons[type] || 'infoCircle';
  };

  const getActivityColor = (type) => {
    const colors = {
      user: 'success',
      company: 'primary',
      system: 'info',
      alert: 'warning',
      edit: 'info',
      delete: 'danger',
      success: 'success',
      warning: 'warning'
    };
    return colors[type] || 'secondary';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  if (loading) {
    return (
      <Card className="activity-feed-card border-0 shadow-sm h-100">
        <Card.Header className="activity-feed-header">
          <h5 className="mb-0">{AP_COPY.ACTIVITY_FEED_TITLE}</h5>
        </Card.Header>
        <Card.Body>
          <div className="activity-timeline">
            {[1, 2, 3].map(i => (
              <div key={i} className="activity-item loading">
                <div className="activity-icon-wrapper">
                  <div className="skeleton skeleton-circle"></div>
                </div>
                <div className="activity-content">
                  <div className="skeleton skeleton-text skeleton-lg mb-2"></div>
                  <div className="skeleton skeleton-text skeleton-sm"></div>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Card className="activity-feed-card border-0 shadow-sm h-100">
        <Card.Header className="activity-feed-header">
          <h5 className="mb-0">{AP_COPY.ACTIVITY_FEED_TITLE}</h5>
        </Card.Header>
        <Card.Body>
          <div className="activity-empty-state">
            <div className="empty-icon">
              <Icon name="infoCircle" size={48} />
            </div>
            <p className="empty-text">{AP_COPY.ACTIVITY_EMPTY}</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="activity-feed-card border-0 shadow-sm h-100">
      <Card.Header className="activity-feed-header">
        <h5 className="mb-0">{AP_COPY.ACTIVITY_FEED_TITLE}</h5>
        {onViewAll && (
          <button className="btn-view-all" onClick={onViewAll}>
            {AP_COPY.BTN_VIEW_ALL}
            <Icon name="arrowRight" size={14} className="ms-1" />
          </button>
        )}
      </Card.Header>
      
      <Card.Body className="activity-feed-body">
        <div className="activity-timeline">
          {displayedActivities.map((activity, index) => {
            const color = activity.color || getActivityColor(activity.type);
            const icon = activity.icon || getActivityIcon(activity.type);
            
            return (
              <div 
                key={activity.id || index} 
                className="activity-item"
              >
                {/* Timeline line */}
                {index < displayedActivities.length - 1 && (
                  <div className="activity-line"></div>
                )}
                
                {/* Icon */}
                <div className={`activity-icon-wrapper bg-${color} bg-opacity-10`}>
                  <div className={`activity-icon text-${color}`}>
                    <Icon name={icon} size={16} />
                  </div>
                </div>
                
                {/* Content */}
                <div className="activity-content">
                  <div className="activity-header">
                    <h6 className="activity-title mb-0">
                      {activity.title}
                    </h6>
                    <span className="activity-time">
                      {formatTimestamp(activity.timestamp)}
                    </span>
                  </div>
                  
                  {activity.description && (
                    <p className="activity-description mb-0">
                      {activity.description}
                    </p>
                  )}
                  
                  {activity.user && (
                    <small className="activity-user">
                      Par <strong>{activity.user}</strong>
                    </small>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ActivityFeed;
